/**
 * Everything the exercise module reads or writes a record through: ids, the
 * name a plan takes, and the ways a length of time is written.
 *
 * It sits beside the feature rather than inside `services/` for the same
 * reason `planMapping` does — none of it is about the wire. The service hands
 * over the record; what a screen makes of it is this.
 */

import type {
  ExerciseEntry,
  ExercisePlanRecord,
} from "./types/exercisePlans.types";

let idCounter = 0;

/**
 * Ids for the rows the dialog creates. They never leave the browser, so a
 * counter is enough — and unlike `Math.random`, two rows added in the same
 * millisecond still differ.
 */
export const createId = (prefix: string): string =>
  `${prefix}-${(idCounter += 1).toString(36)}-${Date.now().toString(36)}`;

/** An empty line, which is what the dialog opens with. */
export const createExerciseEntry = (): ExerciseEntry => ({
  id: createId("exercise"),
  type: "",
  typeId: null,
  minutes: 0,
});

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** "02 Jan, 2026" — how every table in the panel writes a date. */
export const formatExerciseDate = (value: string | null): string => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  const day = String(date.getDate()).padStart(2, "0");
  return `${day} ${MONTHS[date.getMonth()]}, ${date.getFullYear()}`;
};

/**
 * The tail of a plan's name — "100926" out of
 * "Exercise Plan-1/Dia Rode/100926".
 *
 * The backend's name says three things, and a table that is already inside one
 * client, under a column headed Plan name, has already said two of them. So
 * the column shows the part that tells the rows apart, and the whole name is
 * on the row's `title` for anyone who wants it.
 *
 * The last segment rather than a parse of the shape: whatever the backend
 * decides to put in front of it, the part that identifies the plan is what it
 * ends with. A name with no separator in it is its own tail.
 */
export const shortPlanName = (name: string): string => {
  const parts = name.split("/").filter((part) => part.trim());
  return parts.length ? parts[parts.length - 1].trim() : name;
};

/**
 * What a plan written in this sitting is called: "100926" for the tenth of
 * September 2026 — the same six digits the backend's own names end with, so a
 * local plan sits in the list without announcing itself as one.
 *
 * Two digits of year, because it is a label rather than a date to do anything
 * with: the four-digit year is a column over in the table.
 */
export const exercisePlanName = (date: Date): string =>
  [
    String(date.getDate()).padStart(2, "0"),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getFullYear() % 100).padStart(2, "0"),
  ].join("");

/**
 * A length of time as the wire carries one and as the dialog edits one: the
 * two numbers it is picked from, since it is chosen off two lists rather than
 * typed.
 */
export interface DurationParts {
  hours: number;
  minutes: number;
}

export const durationParts = (total: number): DurationParts => ({
  hours: Math.floor(total / 60),
  minutes: total % 60,
});

/**
 * And back to the one figure everything here counts in. Defended, because one
 * side of this is `total_duration` off the wire — a block the backend may send
 * as null, or with either half missing.
 */
export const totalMinutes = (parts: Partial<DurationParts> | null): number => {
  const hours = Number(parts?.hours) || 0;
  const minutes = Number(parts?.minutes) || 0;
  return hours * 60 + minutes;
};

/**
 * "4hr 30m", and the two halves of it — "4hr" and "45m" — when the other is
 * zero. A plan with nothing on it reads "0m" rather than as an empty cell: the
 * column is a duration, and a blank there reads as unknown rather than as none.
 */
export const formatDuration = (total: number): string => {
  const { hours, minutes } = durationParts(total);

  if (hours && minutes) return `${hours}hr ${minutes}m`;
  if (hours) return `${hours}hr`;
  return `${minutes}m`;
};

/** What a plan in hand comes to — the sum of its lines. */
export const planDuration = (plan: ExercisePlanRecord): number =>
  plan.entries.reduce((sum, entry) => sum + entry.minutes, 0);

/**
 * The exercises on a plan, in order, for the line the table draws them on.
 *
 * Lines with nothing picked are dropped: a plan holding a blank row would
 * otherwise put an empty name in the middle of "Cycling, , Running".
 */
export const exerciseTypes = (entries: ExerciseEntry[]): string[] =>
  entries.map((entry) => entry.type.trim()).filter(Boolean);
